import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { groupBookingsService } from '../../../services';
import { useResponsive } from '../../../hooks/useResponsive';
import GroupForm from './GroupForm';
import GroupDetails from './GroupDetails';
import travelieTheme from '../../../styles/travelieTheme';
import './GroupsManagement.css';
import './GroupsManagement-Mobile.css';

// Phase 6: Migrated to TravelieTheme - June 16, 2026

const GroupsManagement = () => {
  const { isMobile, isDesktopOrWide } = useResponsive();

  // Helper function for button styling
  const getButtonStyle = (type, size = 'medium') => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' },
      warning: { ...baseStyle, background: travelieTheme.colors.status.warning, color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };

  // Helper function for status badge styling
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      display: 'inline-block'
    };

    const statusStyles = {
      active: { ...baseStyle, background: '#DCFCE7', color: '#166534' },
      pending: { ...baseStyle, background: '#FEF3C7', color: '#92400E' },
      completed: { ...baseStyle, background: '#E0E7FF', color: '#3730A3' },
      cancelled: { ...baseStyle, background: '#FEE2E2', color: '#991B1B' }
    };

    return statusStyles[status] || baseStyle;
  };
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getAll();
      // Ensure data is always an array
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupCreate = async (groupData) => {
    try {
      await groupBookingsService.create(groupData);
      fetchGroups();
      setShowForm(false);
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group booking. Please try again.');
    }
  };

  const handleViewDetails = (group) => {
    setSelectedGroup(group);
    setShowDetails(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };


  // Filter groups based on status and search term
  const filteredGroups = (Array.isArray(groups) ? groups : []).filter(group => {
    // Status filter
    if (filter !== 'all' && group.status !== filter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        group.title?.toLowerCase().includes(search) ||
        group.destination?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  // Calculate statistics
  const safeGroups = Array.isArray(groups) ? groups : [];
  const stats = {
    total: safeGroups.length,
    active: safeGroups.filter(g => g.status === 'active').length,
    upcoming: safeGroups.filter(g => {
      const startDate = new Date(g.start_date);
      return startDate > new Date() && g.status === 'active';
    }).length,
    totalRevenue: safeGroups.reduce((sum, g) => sum + parseFloat(g.total_revenue || 0), 0)
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: travelieTheme.spacing.md
      }}>
        <div className="loading-spinner"></div>
        <p style={{
          fontSize: travelieTheme.typography.fontSize.sm,
          color: travelieTheme.colors.text.secondary
        }}>Loading group bookings...</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? travelieTheme.spacing.md : travelieTheme.spacing.xl,
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: travelieTheme.spacing.md,
        marginBottom: travelieTheme.spacing.xl
      }}>
        <div>
          <h1 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.xs
          }}>Group Bookings</h1>
          <p style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.secondary
          }}>Manage group travel bookings and track participant registrations</p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          type="button"
          style={getButtonStyle('primary')}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <svg style={{ width: '16px', height: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Group Booking
        </button>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: travelieTheme.spacing.md,
        marginBottom: travelieTheme.spacing.xl
      }}>
        <div style={{
          padding: travelieTheme.spacing.lg,
          background: '#FFFFFF',
          border: `1px solid ${travelieTheme.colors.neutral.border}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.boxShadow
        }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            color: travelieTheme.colors.text.secondary,
            marginBottom: travelieTheme.spacing.sm
          }}>Total Groups</h3>
          <div style={{
            fontSize: '32px',
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: travelieTheme.colors.text.primary
          }}>{stats.total}</div>
        </div>
        <div style={{
          padding: travelieTheme.spacing.lg,
          background: '#DCFCE7',
          border: '1px solid #86EFAC',
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.boxShadow
        }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            color: '#166534',
            marginBottom: travelieTheme.spacing.sm
          }}>Active Groups</h3>
          <div style={{
            fontSize: '32px',
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: '#166534'
          }}>{stats.active}</div>
        </div>
        <div style={{
          padding: travelieTheme.spacing.lg,
          background: '#DBEAFE',
          border: '1px solid #93C5FD',
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.boxShadow
        }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            color: '#1E40AF',
            marginBottom: travelieTheme.spacing.sm
          }}>Upcoming Groups</h3>
          <div style={{
            fontSize: '32px',
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: '#1E40AF'
          }}>{stats.upcoming}</div>
        </div>
        <div style={{
          padding: travelieTheme.spacing.lg,
          background: '#FEF3C7',
          border: '1px solid #FDE047',
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.boxShadow
        }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            color: '#92400E',
            marginBottom: travelieTheme.spacing.sm
          }}>Total Revenue</h3>
          <div style={{
            fontSize: '32px',
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: '#92400E'
          }}>{formatCurrency(stats.totalRevenue)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: travelieTheme.spacing.md,
        marginBottom: travelieTheme.spacing.lg,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <div style={{
          position: 'relative',
          flex: 1
        }}>
          <svg style={{
            position: 'absolute',
            left: travelieTheme.spacing.sm,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: travelieTheme.colors.text.secondary
          }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by group name or destination..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.sm} ${travelieTheme.spacing.sm} 40px`,
              fontSize: travelieTheme.typography.fontSize.sm,
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              borderRadius: travelieTheme.components.card.borderRadius,
              outline: 'none',
              transition: `border-color ${travelieTheme.transitions.fast}`
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
            onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
          />
        </div>
        
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
            fontSize: travelieTheme.typography.fontSize.sm,
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.card.borderRadius,
            outline: 'none',
            cursor: 'pointer',
            background: '#FFFFFF',
            minWidth: isMobile ? '100%' : '180px'
          }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Groups Table */}
      <div style={{
        background: '#FFFFFF',
        border: `1px solid ${travelieTheme.colors.neutral.border}`,
        borderRadius: travelieTheme.components.card.borderRadius,
        boxShadow: travelieTheme.components.card.boxShadow,
        overflow: 'hidden'
      }}>
        {filteredGroups.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: `${travelieTheme.spacing.xl} ${travelieTheme.spacing.md}`
          }}>
            <svg style={{
              width: '64px',
              height: '64px',
              margin: '0 auto',
              marginBottom: travelieTheme.spacing.md,
              color: travelieTheme.colors.text.secondary
            }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 style={{
              fontSize: travelieTheme.typography.fontSize.lg,
              fontWeight: travelieTheme.typography.fontWeight.semibold,
              color: travelieTheme.colors.text.primary,
              marginBottom: travelieTheme.spacing.xs
            }}>No group bookings found</h3>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary
            }}>Create your first group booking to get started</p>
          </div>
        ) : (
          <table className="groups-table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Destination</th>
                <th>Travel Dates</th>
                <th>Status</th>
                <th>Bookings</th>
                <th>Revenue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map(group => (
                <tr key={group.id}>
                  <td data-label="Group Name">
                    <div>
                      <div className="group-title">{group.title}</div>
                      <div className="group-destination">{group.component_type}</div>
                    </div>
                  </td>
                  <td data-label="Destination">{group.destination}</td>
                  <td data-label="Travel Dates">
                    {formatDate(group.start_date)} - {formatDate(group.end_date)}
                  </td>
                  <td data-label="Status">
                    <span style={getStatusBadgeStyle(group.status)}>
                      {group.status.charAt(0).toUpperCase() + group.status.slice(1)}
                    </span>
                  </td>
                  <td data-label="Bookings">
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: travelieTheme.spacing.xs
                    }}>
                      <svg style={{ width: '16px', height: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {group.participant_count || 0}
                    </div>
                  </td>
                  <td data-label="Revenue" style={{
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: travelieTheme.colors.status.success
                  }}>
                    {formatCurrency(group.total_revenue)}
                  </td>
                  <td data-label="Actions">
                    <div style={{
                      display: 'flex',
                      gap: travelieTheme.spacing.xs,
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => handleViewDetails(group)}
                        title="View Details"
                        style={getButtonStyle('primary', 'small')}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        View
                      </button>
                      <button
                        onClick={() => window.open(group.public_url, '_blank')}
                        title="View Public Page"
                        style={getButtonStyle('success', 'small')}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => {
                          if (navigator.clipboard) {
                            navigator.clipboard.writeText(group.public_url);
                            alert('Public URL copied to clipboard!');
                          }
                        }}
                        title="Copy Public URL"
                        style={getButtonStyle('warning', 'small')}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        Copy
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Group Form Modal */}
      {showForm && (
        <div 
          onClick={() => setShowForm(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: travelieTheme.spacing.md
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF',
              borderRadius: travelieTheme.components.card.borderRadius,
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <GroupForm 
              onClose={() => setShowForm(false)}
              onSubmit={handleGroupCreate}
            />
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      {showDetails && selectedGroup && (
        <div 
          onClick={() => setShowDetails(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: travelieTheme.spacing.md
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF',
              borderRadius: travelieTheme.components.card.borderRadius,
              maxWidth: '1000px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <GroupDetails 
              group={selectedGroup}
              onClose={() => setShowDetails(false)}
              onUpdate={fetchGroups}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsManagement;
